import React from 'react';
import { ConfigService } from '@/configuration/services/configService';
import { SAML_LOGIN_TYPE } from '@srm/core/auth';
import { lazyRoute } from '@srm/ui/lib/lazyRoute';
import { RouteNames } from './router.types';

const loadAdministrationPages = () => import('./lazy/administrationPages');
const loadAuthPages = () => import('./lazy/authPages');
const loadDashboardPages = () => import('./lazy/dashboardPages');
const loadNotificationPages = () => import('./lazy/notificationPages');
const loadReferencePages = () => import('./lazy/referencePages');
const loadWorkspacePages = () => import('./lazy/workspacePages');

const Login = lazyRoute(loadAuthPages, 'Login');
const Dashboard = lazyRoute(loadWorkspacePages, 'Dashboard');
const Documentation = lazyRoute(loadReferencePages, 'Documentation');
const Documents = lazyRoute(loadWorkspacePages, 'Documents');
const Dictionaries = lazyRoute(loadReferencePages, 'Dictionaries');
const Reports = lazyRoute(loadWorkspacePages, 'Reports');
const Document = lazyRoute(loadWorkspacePages, 'Document');
const DocumentSupportPreview = lazyRoute(loadWorkspacePages, 'DocumentSupportPreview');
const Tasks = lazyRoute(loadWorkspacePages, 'Tasks');
const Search = lazyRoute(loadWorkspacePages, 'Search');
const Notification = lazyRoute(loadNotificationPages, 'Notification');
const Roles = lazyRoute(loadAdministrationPages, 'Roles');
const ReleaseNotes = lazyRoute(loadReferencePages, 'ReleaseNotes');
const DynamicGroups = lazyRoute(loadAdministrationPages, 'DynamicGroups');
const SetAssistantSimple = lazyRoute(loadAdministrationPages, 'SetAssistantSimple');
const NotificationSettings = lazyRoute(loadNotificationPages, 'NotificationSettings');
const CustomUserSettings = lazyRoute(loadAdministrationPages, 'CustomUserSettings');
const RedirectsAndReplacements = lazyRoute(loadAdministrationPages, 'RedirectsAndReplacements');
const SignUp = lazyRoute(loadAuthPages, 'SignUp');
const SignUpConfirm = lazyRoute(loadAuthPages, 'SignUpConfirm');
const ChangeEmailConfirm = lazyRoute(loadAuthPages, 'ChangeEmailConfirm');
const ResetPasswordRequest = lazyRoute(loadAuthPages, 'ResetPasswordRequest');
const ResetPasswordConfirm = lazyRoute(loadAuthPages, 'ResetPasswordConfirm');
const Settings = lazyRoute(loadAdministrationPages, 'Settings');
const UsersActivationPage = lazyRoute(loadAdministrationPages, 'UsersActivationPage');
const AdfsSamlSsoSigInRedirect = lazyRoute(loadAuthPages, 'AdfsSamlSsoSigInRedirect');
const AdfsSamlSsoSigOutRedirect = lazyRoute(loadAuthPages, 'AdfsSamlSsoSigOutRedirect');
const AdfsSamlSsoSignIn = lazyRoute(loadAuthPages, 'AdfsSamlSsoSignIn');
const ManagementDashboards = lazyRoute(loadDashboardPages, 'ManagementDashboards');
const ManagementDashboardView = lazyRoute(loadDashboardPages, 'ManagementDashboardView');

export interface IRoute {
    path: string;
    component: React.ComponentType;
    /** Маршрут требует валидный access, но не загружает профиль через обычные API. */
    accessTokenOnly?: boolean;
}

const config = ConfigService.get();

export const publicRoutes: IRoute[] = [{ path: RouteNames.LOGIN, component: Login }];

if (config.application.selfRegistration.restorePasswordEnabled) {
    publicRoutes.push({ path: RouteNames.RESET_PASSWORD_REQUEST, component: ResetPasswordRequest });
    publicRoutes.push({ path: RouteNames.RESET_PASSWORD_CONFIRM, component: ResetPasswordConfirm });
}
if (config.application.selfRegistration.signUpEnabled) {
    publicRoutes.push({ path: RouteNames.SIGN_UP, component: SignUp });
    publicRoutes.push({ path: RouteNames.SIGN_UP_CONFIRM, component: SignUpConfirm });
    publicRoutes.push({ path: RouteNames.CHANGE_EMAIL_CONFIRM, component: ChangeEmailConfirm });
}

export const privateRoutes: IRoute[] = [
    { path: RouteNames.DOCUMENTATION, component: Documentation },
    { path: RouteNames.DOCUMENTATION_ITEM, component: Documentation },
    { path: RouteNames.DASHBOARD, component: Dashboard },
    { path: RouteNames.DOCUMENTS, component: Documents },
    { path: RouteNames.DICTIONARIES, component: Dictionaries },
    { path: RouteNames.REPORTS, component: Reports },
    { path: RouteNames.DOCUMENT, component: Document },
    {
        path: RouteNames.DOCUMENT_SUPPORT_PREVIEW,
        component: DocumentSupportPreview,
        accessTokenOnly: true,
    },
    { path: RouteNames.DOCUMENT_CREATE_ON_BASE, component: Document },
    { path: RouteNames.DOCUMENT_CREATE_ON_BASE_TEMPLATE, component: Document },
    { path: RouteNames.DOCUMENT_CREATE_BY_ACTION, component: Document },
    { path: RouteNames.DOCUMENT_CREATE_BY_ACTION_TEMPLATE, component: Document },
    { path: RouteNames.TASKS, component: Tasks },
    { path: RouteNames.SEARCH, component: Search },
    { path: RouteNames.NOTIFICATION, component: Notification },
    { path: RouteNames.ROLES, component: Roles },
    { path: RouteNames.REDIRECTS_AND_REPLACEMENTS, component: RedirectsAndReplacements },
    { path: RouteNames.SETTINGS_NOTIFICATIONS, component: NotificationSettings },
    { path: RouteNames.CUSTOMUSERSETTINGS, component: CustomUserSettings },
    { path: RouteNames.SET_ASSISTANT_SIMPLE, component: SetAssistantSimple },
    { path: RouteNames.RELEASENOTES, component: ReleaseNotes },
    { path: RouteNames.ADMINISTRATIONS_DYNGROUPS, component: DynamicGroups },
    // «Мои настройки» есть всегда: раздел «Доступ поддержки» нужен и без самостоятельной регистрации (на него ведёт
    // уведомление support.access); остальные разделы страница показывает по флагам CanView с сервера.
    { path: RouteNames.SETTINGS, component: Settings },
];

if (config.application.selfRegistration.signUpEnabled) {
    privateRoutes.push({ path: RouteNames.USERS_ACTIVATION, component: UsersActivationPage });
    privateRoutes.push({ path: RouteNames.CHANGE_EMAIL_CONFIRM, component: ChangeEmailConfirm });
}

// Управленческие дашборды (Stimulsoft) включены по умолчанию; выключает их только явный
// dashboards.enabled=false в конфигурации — тогда ни маршрута, ни чанка с вьюером
// (бэкенд с тем же AppSettings:Dashboards:Enabled=false отдаёт 404 и прячет раздел меню).
if (config.application.dashboards?.enabled !== false) {
    privateRoutes.push({ path: RouteNames.DASHBOARDS, component: ManagementDashboards });
    privateRoutes.push({ path: RouteNames.DASHBOARD_VIEW, component: ManagementDashboardView });
    privateRoutes.push({ path: RouteNames.DASHBOARD_LEGACY_VIEW, component: ManagementDashboardView });
}

if (config.application.authentication.controls.enabled.includes(SAML_LOGIN_TYPE)) {
    //
    publicRoutes.push({ path: RouteNames.ADFS_SAML_SSO_SIGN_IN_REDIRECT, component: AdfsSamlSsoSigInRedirect });
    publicRoutes.push({ path: RouteNames.ADFS_SAML_SSO_SIGN_IN, component: AdfsSamlSsoSignIn });
    //
    privateRoutes.push({ path: RouteNames.ADFS_SAML_SSO_SIGN_OUT_REDIRECT, component: AdfsSamlSsoSigOutRedirect });
}

// DEV-витрина состояний таба «Ход согласования» на мок-данных. Вся ветка (вместе с
// динамическим import) вырезается из prod-сборки по import.meta.env.DEV.
if (import.meta.env.DEV) {
    publicRoutes.push({
        path: '/dev/route-ledger',
        component: React.lazy(() => import('@pages/dev/RouteLedgerGallery')),
    });
}
